// Pesquisa da Central de Solicitações (#6570 e #6571).
//
// Texto livre procura no título, na descrição e nos comentários (a conclusão é um
// comentário "✅ Conclusão: ..."), sem diferenciar acento nem maiúscula. Começar com
// "UC" procura pelo número da UC do jeito que o Faturamento casa as faturas
// (src/lib/ucCodigo.ts): pontos, traço, espaço e zeros na frente não contam.
//
// Sem imports de runtime: roda sob `node --test`.

export type BuscaSolicitacao =
    | { tipo: 'vazia' }
    | { tipo: 'texto'; termo: string }
    | { tipo: 'uc'; numero: string };

// \p{M} (marca combinante) e não \p{Diacritic}: este também apaga ^ ` ´ ¨ do texto, e o
// banco (regexBuscaTexto) não consegue apagar do comentário o que o termo ignorou.
const semAcento = (v: string): string =>
    v.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

// #6586: ponto, traço, vírgula e barra não contam ("123456" acha "1.234,56").
const SINAIS = /[.,\-/]/g;

/** Texto como a pesquisa compara: sem acento, minúsculo e sem . , - / */
export const normalizarBusca = (v: string): string => semAcento(v).replace(SINAIS, '');

/** Número de UC como o Faturamento compara: só dígitos, sem zeros na frente. */
export const numeroUcNormalizado = (v: string | null | undefined): string =>
    String(v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');

export function interpretarBusca(texto: string | null | undefined): BuscaSolicitacao {
    const t = String(texto ?? '').trim();
    if (!t) return { tipo: 'vazia' };
    const uc = t.match(/^uc\s*[:#-]?\s*([\d.\-\s]+)$/i);
    if (uc) {
        const numero = numeroUcNormalizado(uc[1]);
        // Menos de 4 dígitos acharia qualquer número solto no texto.
        if (numero.length >= 4) return { tipo: 'uc', numero };
    }
    // trim depois de tirar a pontuação: "boleto -" deixaria "boleto " e exigiria um espaço depois.
    const termo = normalizarBusca(t).trim();
    return termo ? { tipo: 'texto', termo } : { tipo: 'vazia' };
}

export function textoContem(texto: string | null | undefined, termoNormalizado: string): boolean {
    return !!texto && normalizarBusca(texto).includes(termoNormalizado);
}

/**
 * Os números de UC escritos no texto, já normalizados. Junta dígitos separados por
 * ponto ou traço ("1.500.203.012-60" vira "150020301260").
 *
 * Numa segunda leitura junta também por espaço ("595 892 012 17"). Só a segunda não
 * serve: uma lista de UCs, uma por linha ou separada por espaço, viraria um número só
 * e nenhuma das UCs seria achada. Por isso as duas leituras valem.
 */
export function numerosUcNoTexto(texto: string | null | undefined): string[] {
    const t = String(texto ?? '');
    const achados = new Set<string>();
    for (const juntar of [/(\d)[.\-]+(?=\d)/g, /(\d)[.\-\s]+(?=\d)/g]) {
        for (const n of t.replace(juntar, '$1').match(/\d{4,}/g) ?? []) {
            const semZeros = n.replace(/^0+/, '');
            if (semZeros) achados.add(semZeros);
        }
    }
    return [...achados];
}

export function textoCitaUc(texto: string | null | undefined, numeros: readonly string[]): boolean {
    if (!texto || numeros.length === 0) return false;
    const achados = numerosUcNoTexto(texto);
    return achados.some(n => numeros.includes(n));
}

// Um caractere qualquer seguido do começo do texto: não casa com nada (vale no Postgres e no JS).
const NUNCA_CASA = '.^';

/**
 * Expressão para o banco (operador imatch do PostgREST) que acha o número nos
 * comentários escrito de qualquer jeito: zeros na frente e ponto, traço ou espaço
 * entre os dígitos. As bordas evitam achar o número dentro de outro maior.
 *
 * ⚠️ Sem parênteses nem vírgula, de propósito: o resultado entra num `.or(...)` e o
 * PostgREST corta o valor sem aspas no primeiro `)` ou `,` (o banco devolvia "parentheses
 * () not balanced" e a busca por UC nos comentários voltava sempre vazia). Por isso as
 * bordas são quatro alternativas em vez de `(^|[^0-9])...([^0-9]|$)`, e o caractere que
 * não é dígito (o cadastro tem UC como "1,123456" e "A / B") entra como \xHH.
 */
export function regexNumeroUc(numero: string): string {
    if (!/[0-9]/.test(numero)) return NUNCA_CASA;
    const digitos = [...numero]
        .map(c => /[0-9]/.test(c) ? c : `\\x${c.codePointAt(0)!.toString(16)}`)
        .join('[.\\- ]?');
    const nucleo = `0*${digitos}`;
    return [`^${nucleo}$`, `^${nucleo}[^0-9]`, `[^0-9]${nucleo}$`, `[^0-9]${nucleo}[^0-9]`].join('|');
}

// Letra sem acento → a classe que aceita as versões com acento no banco. Cobre o Latin-1
// inteiro (tudo que o NFD de normalizarBusca reduz a uma letra); fora dele (š, ž, č) o
// comentário não casa no banco, e a classe maior estouraria o tamanho da URL.
const ACENTOS: Record<string, string> = {
    a: 'aàáâãäå', e: 'eèéêë', i: 'iìíîï', o: 'oòóôõö', u: 'uùúûü', c: 'cç', n: 'nñ', y: 'yýÿ',
};

/**
 * Expressão para o banco (imatch) equivalente a textoContem: aceita acento e
 * . , - / entre quaisquer letras do termo. Metacaracteres do termo (%, (, + ...)
 * são procurados como texto.
 */
export function regexBuscaTexto(termoNormalizado: string): string {
    // Por caractere (e não slice na string): cortar no meio de um emoji deixaria meia letra no URL.
    return [...termoNormalizado].slice(0, 100).map(ch => {
        if (ACENTOS[ch]) return `[${ACENTOS[ch]}]`;
        return /[a-z0-9 ]/.test(ch) ? ch : `\\${ch}`;
    }).join('[.,/-]*');
}
